import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { push } from "react-router-redux";
import { connect } from "react-redux";
import { getFirstNameLastInitial, truncate } from "LIB/formatters/text";
import { shortDateAndTimestamp } from "LIB/formatters/date";
import {
  DataTable,
  CheckColumn,
  SearchPropTypes,
  ToggleButton,
  NoteTooltip as IconTooltip,
  PartNumber,
  CompanyName,
  LocationFlag
} from "MODULES/shared/components";
import { condition } from "MODULES/SalesOrders/constants";
import { RECORD_TYPES } from "MODULES/OutgoingShipments/constants";
import withCurrentUser, {
  WithCurrentUser
} from "MODULES/session/decorators/withCurrentUser";
import MultiLocationFeature from "MODULES/shared/components/MultiLocationFeature/MultiLocationFeature";
import { subscribe, unsubscribe } from "MODULES/Shipping/actions";
import { reloadShippingSilent, toggleCheck } from "../../actions";

function renderRow(r, pushHistory, currentUser, check) {
  const refId = r.reference_record_uuid || r.reference_record_id;
  const refs = {
    ManagedRepairOrder: "managed-repair-orders",
    MistakeShipment: "mistake-shipments",
    PurchaseOrder: "purchase-orders",
    ReturnedCore: "purchase-orders",
    RepairOrder: "repair-orders",
    Rma: "rmas",
    ReturnToCustomerRecord: "rmas",
    SalesOrder: "sales-orders"
  };

  return (
    <tr
      key={r.id}
      className={`clickable ${r.cancelled && "text-muted"}`}
      onClick={() => pushHistory(`/shipping/${r.uuid}`)}
    >
      <td>
        {r.reference_record_exporting && (
          <IconTooltip
            icon="fa-globe"
            placement="top"
            note="Exporting"
            color="#FFFFFF"
          />
        )}
      </td>
      <td>{r.code}</td>
      <td>{shortDateAndTimestamp(r.released_at || r.created_at)}</td>
      <td>{shortDateAndTimestamp(r.created_at)}</td>
      <td>
        {
          RECORD_TYPES[
            r.reference_record_type || r.reference_record_line_item_type
          ]
        }
      </td>
      <td>
        <CompanyName flattenedCompany={r} />
      </td>
      <td>
        {r.qc_documents_sent && (
          <span className="label label-success">
            <i className="fa fa-check" />
            &nbsp;&nbsp; Sent
          </span>
        )}
      </td>
      <td className="text-center">
        {r.formatted_address && (
          <IconTooltip note={r.formatted_address} icon="fa-truck" />
        )}
      </td>
      <MultiLocationFeature>
        <td>
          <LocationFlag location={r.store_location_id} />
        </td>
      </MultiLocationFeature>
      <td>
        <PartNumber part={r.part} />
      </td>
      <td>
        {r.inventory && (
          <Link
            onClick={e => e.stopPropagation()}
            to={`/inventories/${r.inventory.uuid || r.inventory.id}`}
          >
            {r.serial_number}
          </Link>
        )}
      </td>
      <td>
        <Link
          onClick={e => e.stopPropagation()}
          to={`/${refs[
            r.reference_record_type || r.reference_record_line_item_type
          ]}/${refId}`}
        >
          {r.reference_record_code}
        </Link>
      </td>
      <td>{r.customer_po ? r.customer_po : "N/A"}</td>
      <CheckColumn checked={r.discreet} icon="eye-slash" text="Yes" />
      <td>{condition(r.condition).abbrev}</td>
      <td className="text-center">{r.quantity_shipped}</td>
      <td>{truncate(r.outgoing_awb_number, 24)}</td>
      <td>{r.checked && shortDateAndTimestamp(r.checked_at)}</td>
      <td>{r.checked && getFirstNameLastInitial(r.checked_by_full_name)}</td>
      <td>
        {r.dropshipped ? (
          "Dropshipment"
        ) : (
          <ToggleButton
            text="Day-End Check"
            toggledText="Uncheck"
            toggled={r.checked}
            canRemove={r.checked_by_id !== currentUser.id || r.checked}
            handleClick={e => {
              e.stopPropagation();
              check(r, currentUser);
            }}
          />
        )}
      </td>
    </tr>
  );
}

export function Table({
  results,
  pushHistory,
  check,
  currentUser,
  isLoading,
  subscribeToOrdersChannel,
  unsubscribeToOrdersChannel
}) {
  useEffect(() => {
    subscribeToOrdersChannel();

    return () => unsubscribeToOrdersChannel();
  }, []);

  return (
    <DataTable loading={isLoading}>
      <DataTable.Head>
        <th />
        <th>Ship #</th>
        <th>Date Released</th>
        <th>Date Shipped</th>
        <th>Type</th>
        <th>Company</th>
        <th>Sent QC Docs</th>
        <th className="text-center">Ship To</th>
        <MultiLocationFeature>
          <th>Store Loc</th>
        </MultiLocationFeature>
        <th>Part #</th>
        <th>Serial #</th>
        <th>Ref #</th>
        <th>Customer PO#</th>
        <th>Discreet</th>
        <th>CD Sent</th>
        <th className="text-center">QTY SHIPPED</th>
        <th>Outgoing AWB #</th>
        <th>Checked At</th>
        <th>Checked By</th>
        <th />
      </DataTable.Head>
      <DataTable.Body>
        {results.map(r => renderRow(r, pushHistory, currentUser, check))}
      </DataTable.Body>
    </DataTable>
  );
}

Table.propTypes = {
  pushHistory: PropTypes.func.isRequired,
  ...WithCurrentUser,
  ...SearchPropTypes
};

export default connect(undefined, (dispatch, ownProps) => ({
  check: (shipment, currentUser) =>
    dispatch(toggleCheck(shipment, currentUser)),
  refresh: ownProps.pageActions.expire,
  pushHistory: e => dispatch(push(e)),
  subscribeToOrdersChannel: () => dispatch(subscribe(reloadShippingSilent)),
  unsubscribeToOrdersChannel: () => dispatch(unsubscribe())
}))(withCurrentUser(Table));



// WEBPACK FOOTER //
// ./app/modules/OutgoingShipments/components/Search/Table.jsx